import React, { useState } from "react";
import { toast } from "react-toastify";
import { api } from "../../utils/api";
import { API_ENDPOINTS } from "../../constants/api";
import MediaComponent from "../MediaComponent";

function InlineSlideUpload({ client, onUploadSuccess, onCancel }) {
  const [showForm, setShowForm] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [customName, setCustomName] = useState("");
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Add preview URL for display with file type information
      const fileWithPreview = {
        file: file, // Keep reference to original file
        name: file.name,
        size: file.size,
        type: file.type,
        preview: URL.createObjectURL(file),
      };
      setSelectedFile(fileWithPreview);
    }
  };

  const handleCancel = () => {
    if (selectedFile && selectedFile.preview) {
      URL.revokeObjectURL(selectedFile.preview);
    }
    setSelectedFile(null);
    setCustomName("");
    setShowForm(false);
    if (onCancel) onCancel();
  };

  const handleSubmit = async () => {
    if (!selectedFile || !customName.trim()) {
      toast.error("Please select a file and provide a custom name");
      return;
    }

    setUploading(true);

    try {
      const loadingToast = toast.loading(
        "Uploading client files... Please do not close this page",
        { position: "top-right" }
      );

      const folderName = client.client_name.split(" ").join("_");

      const signedUrlResponse = await api.post(
        API_ENDPOINTS.FILES.GET_SIGNED_URL,
        {
          fileName: selectedFile.name,
          fileType: selectedFile.type,
          folderName,
        }
      );

      if (!signedUrlResponse.ok) {
        throw new Error("Failed to get signed URL");
      }

      const url = signedUrlResponse.data.url;
      const pushResults = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": selectedFile.type,
        },
        body: selectedFile.file,
      });

      //   check if push results is ok
      if (!pushResults.ok) {
        throw new Error("Failed to upload file to storage");
      }

      //   upload to the db

      const newClient = {
        ...client,
        client_slides: [
          ...client.client_slides,
          {
            location: `${process.env.REACT_APP_S3_LINK}${folderName}/${selectedFile.name}`,
            description: customName,
          },
        ],
      };

      // Update the database with the correct structure
      const data = {
        client_slides: newClient.client_slides,
        client_name: newClient.client_name,
        client_brief: newClient.client_brief,
      };

      const dbResponse = await api.post(
        API_ENDPOINTS.CLIENTS.CREATE_UPDATE,
        data
      );

      // check if all database response is ok
      if (!dbResponse.data.success) {
        toast.dismiss(loadingToast);
        toast.error(
          "Failed to update database with file information. Please try again.",
          {
            position: "top-right",
            autoClose: 3000,
          }
        );
      }
      toast.dismiss(loadingToast);
      toast.success("File uploaded and database updated successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      setUploading(false);
      onUploadSuccess(newClient);
      if (selectedFile.preview) {
        URL.revokeObjectURL(selectedFile.preview);
      }

      setSelectedFile(null);
      setCustomName("");
      setShowForm(false);
    } catch (error) {
      console.error("Error uploading slide:", error);
      toast.error(error.response?.data?.message || "Failed to upload slide");
    } finally {
      setUploading(false);
    }
  };

  if (!showForm) {
    return (
      <div className="slide-card upload-more-card">
        <div className="upload-more-content">
          <div className="upload-more-icon">
            <span>📤</span>
          </div>
          <h4>Upload More Slides</h4>
          <p>Add additional slides to this client's presentation</p>
          <button
            className="btn-primary upload-more-btn"
            onClick={() => setShowForm(true)}>
            <span>➕</span> Add Slide
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="slide-card add-slide-form-card">
      <div className="add-slide-form">
        <div className="form-header">
          <h4>Add New Slide</h4>
          <button
            className="btn-close"
            onClick={handleCancel}
            disabled={uploading}>
            ✕
          </button>
        </div>

        <div className="file-upload-section">
          <input
            type="file"
            id="slide-file-input"
            accept="image/*,video/*"
            onChange={handleFileSelect}
            style={{ display: "none" }}
            disabled={uploading}
          />

          {!selectedFile ? (
            <label
              htmlFor="slide-file-input"
              className="file-drop-zone">
              <div className="drop-zone-content">
                <span className="upload-icon">📁</span>
                <p>Click to select a file</p>
                <small>Supports images and videos</small>
              </div>
            </label>
          ) : (
            <div className="file-preview-section">
              <div className="preview-container">
                <MediaComponent
                  src={selectedFile.preview}
                  alt="New slide preview"
                  className="new-slide-preview"
                  fileType={selectedFile.type}
                  isPreview={true}
                  autoPlay={false}
                  controls={false}
                  muted={true}
                />
              </div>
              <button
                className="change-file-btn"
                onClick={() => {
                  if (selectedFile.preview) {
                    URL.revokeObjectURL(selectedFile.preview);
                  }
                  setSelectedFile(null);
                }}
                disabled={uploading}>
                Change File
              </button>
            </div>
          )}
        </div>

        <div className="custom-name-section">
          <label htmlFor="slide-custom-name">Custom Name:</label>
          <input
            type="text"
            id="slide-custom-name"
            className="slide-custom-name-input"
            placeholder="Enter a custom name for this slide..."
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            disabled={uploading}
          />
        </div>

        <div className="form-actions">
          <button
            className="btn-secondary"
            onClick={handleCancel}
            disabled={uploading}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleSubmit}
            disabled={uploading || !selectedFile || !customName.trim()}>
            {uploading ? "Uploading..." : "Add Slide"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default InlineSlideUpload;
